import type { ReactNode } from 'react';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import BookmarkIcon from '@mui/icons-material/BookmarkBorder';
import InventoryIcon from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import PaletteIcon from '@mui/icons-material/Palette';
import BrushIcon from '@mui/icons-material/Brush';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import PlaceIcon from '@mui/icons-material/Place';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BadgeIcon from '@mui/icons-material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/AdminPanelSettings';
import {
  InventoryPermission,
  Module,
  type InventoryPermissionId,
  type ModuleId,
} from '@/shared/auth/modules';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  /** Which module claim gates this item. */
  module: ModuleId;
  permission?: InventoryPermissionId;
  /** Search aliases only; these never affect routes or authorization. */
  keywords?: string[];
}

export interface NavSection {
  title: string;
  items: NavItem[];
  /** A single-destination section is clearer without a disclosure control. */
  collapsible?: boolean;
}

/**
 * Navigation, grouped the way the warehouse actually works: daily movements
 * first, then the reports built from them, then the data that rarely changes.
 *
 * Dashboard is gated on Inventory - the backend has no dedicated dashboard
 * module ordinal, and the summary it renders is inventory data.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    collapsible: false,
    items: [
      {
        label: 'Dashboard',
        to: '/',
        icon: <DashboardIcon />,
        module: Module.Inventory,
        permission: InventoryPermission.Dashboard,
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      {
        label: 'Stock In',
        to: '/stock-in',
        icon: <LoginIcon />,
        module: Module.StockIn,
        keywords: ['receiving', 'inbound'],
      },
      {
        label: 'Stock Out',
        to: '/stock-out',
        icon: <LogoutIcon />,
        module: Module.StockOut,
        keywords: ['dispatch', 'outbound', 'shipping'],
      },
      {
        label: 'Transfers',
        to: '/stock-transfer',
        icon: <SwapHorizIcon />,
        module: Module.StockTransfer,
      },
      {
        label: 'Adjustments',
        to: '/stock-adjustment',
        icon: <TuneIcon />,
        module: Module.StockAdjustment,
        keywords: ['correction'],
      },
      {
        label: 'Reservations',
        to: '/stock-reservation',
        icon: <BookmarkIcon />,
        module: Module.StockReservation,
        keywords: ['allocated', 'allocation'],
      },
    ],
  },
  {
    title: 'Inventory',
    items: [
      {
        label: 'Movements',
        to: '/inventory',
        icon: <InventoryIcon />,
        module: Module.Inventory,
        permission: InventoryPermission.Movements,
      },
      {
        label: 'By Location',
        to: '/inventory/summary',
        icon: <InventoryIcon />,
        module: Module.Inventory,
        permission: InventoryPermission.ByLocation,
      },
      {
        label: 'By Product',
        to: '/inventory/by-product',
        icon: <InventoryIcon />,
        module: Module.Inventory,
        permission: InventoryPermission.ByProduct,
      },
      {
        label: 'Date Range',
        to: '/inventory/date-based',
        icon: <InventoryIcon />,
        module: Module.Inventory,
        permission: InventoryPermission.DateRange,
      },
    ],
  },
  {
    title: 'Catalogue',
    items: [
      { label: 'Products', to: '/products', icon: <InventoryIcon />, module: Module.Product },
      {
        label: 'Categories',
        to: '/categories',
        icon: <CategoryIcon />,
        module: Module.Category,
      },
      { label: 'Colours', to: '/colours', icon: <PaletteIcon />, module: Module.Colour },
      { label: 'Designs', to: '/designs', icon: <BrushIcon />, module: Module.Design },
      { label: 'Sizes', to: '/sizes', icon: <StraightenIcon />, module: Module.Size },
      {
        label: 'Stock Groups',
        to: '/carton-sizes',
        icon: <ViewInArIcon />,
        module: Module.StockGroup,
      },
      {
        label: 'Client Codes',
        to: '/client-codes',
        icon: <BadgeIcon />,
        module: Module.ClientCode,
      },
    ],
  },
  {
    title: 'Facilities',
    items: [
      {
        label: 'Warehouses',
        to: '/warehouses',
        icon: <WarehouseIcon />,
        module: Module.Warehouse,
      },
      { label: 'Locations', to: '/locations', icon: <PlaceIcon />, module: Module.Location },
    ],
  },
  {
    title: 'Administration',
    items: [
      { label: 'Users', to: '/users', icon: <PeopleIcon />, module: Module.User },
      { label: 'Roles', to: '/user-roles', icon: <SecurityIcon />, module: Module.UserRole },
    ],
  },
];

/** Return the first sidebar destination the current user is allowed to open. */
export function firstPermittedNavigationPath(
  canModule: (module: ModuleId) => boolean,
  canPermission: (permission: InventoryPermissionId) => boolean,
  exclude = new Set<string>(),
): string | null {
  for (const item of NAV_SECTIONS.flatMap((section) => section.items)) {
    if (
      !exclude.has(item.to) &&
      canModule(item.module) &&
      (!item.permission || canPermission(item.permission))
    ) {
      return item.to;
    }
  }
  return null;
}
